import logo from '/ellio-logo.png';
import logoOnly from '/ellio-logo-only.png';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useMenu from '@/hooks/use-menu';
import { cn } from '@/lib/cn';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { menuSections } from '@/constants/menus';
import { CompanySelector } from './company-selector';
import type { IMenu } from '@/types/menu';

/**
 * The menu while the tenant's flags load, on first paint and on every tenant
 * switch. Same sections, paddings and row height as the real menu, one row per
 * entry the role may see, so the real menu replaces it in one paint with
 * little movement: the rows of switched-off modules are the only change.
 */
function SidebarMenuSkeleton({
  collapsed,
  rowsInSection,
}: {
  collapsed: boolean;
  rowsInSection: (menus: IMenu[]) => number;
}) {
  return (
    <div role="status" aria-label="Loading menu">
      {menuSections.map((section, sectionIndex) => {
        const rows = rowsInSection(section.menus);
        if (rows === 0) return null;

        const showsLabel = section.label && !collapsed;

        return (
          <div
            key={section.id}
            className="border-neutral-80 border-b last:border-b-0"
          >
            {showsLabel && (
              <div className="box-content flex h-7 items-center px-[22px] pt-6 pb-2">
                <Skeleton className="h-4 w-24" />
              </div>
            )}

            <ul
              className={cn(
                'space-y-2 px-[22px]',
                sectionIndex === 0 ? 'py-8' : 'pb-8',
                showsLabel ? 'pt-2' : 'pt-8'
              )}
            >
              {/* One wrapper, as the real menu's `Accordion`: the list's
                  spacing falls around it, not between the rows. */}
              <div>
                {Array.from({ length: rows }, (_, row) => (
                  <li
                    key={row}
                    // The real rows' height: the label line sets it, and a
                    // collapsed rail hides the label.
                    className={cn(
                      'flex items-center gap-3 p-2',
                      collapsed ? 'h-8' : 'h-9'
                    )}
                  >
                    <Skeleton className="size-4 flex-none" />
                    {!collapsed && <Skeleton className="h-3.5 w-28" />}
                  </li>
                ))}
              </div>
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function CmsSidebar() {
  const navigate = useNavigate();

  const {
    collapseSidebar,
    setCollapseSidebar,
    getVisibleMenusInSection,
    accordion,
    setAccordion,
    currentChildMenu,
    isMenuActive,
    canViewMenu,
    isAvailable,
    getVisibleTabs,
    hasSectionNav,
    isMenuLoading,
  } = useMenu();

  /**
   * Entries the role may see before the release flags are known: the
   * skeleton's row count.
   */
  const countPermittedMenus = (sectionMenus: IMenu[]) =>
    sectionMenus.filter(menu => {
      if (menu.key) return canViewMenu(menu.key);
      if (menu.children) {
        return menu.children.some(child => canViewMenu(child.key));
      }
      return true;
    }).length;

  /**
   * A tabbed menu is one link here: its children are the page's tabs, drawn by
   * `SectionTabs`, so the sidebar neither lists them nor opens a dropdown.
   */
  const getSidebarChildren = (menu: IMenu) =>
    menu.tabs
      ? []
      : (menu.children ?? []).filter(
          child => canViewMenu(child.key) && isAvailable(child)
        );

  /** Where a childless entry goes: a tabbed one opens its first visible tab. */
  const getMenuTarget = (menu: IMenu) =>
    getVisibleTabs(menu)[0]?.route ?? menu.route;

  useEffect(() => {
    if (collapseSidebar) {
      setAccordion(undefined);
    }
  }, [collapseSidebar, setAccordion]);

  // A page with its own section nav (Settings -> Company) beside an open
  // sidebar is two navigation columns squeezing the form: fold the sidebar to
  // its rail on the way in. Only on entering, so tab hops inside the section
  // respect a sidebar the user opened again.
  useEffect(() => {
    if (hasSectionNav) setCollapseSidebar(true);
  }, [hasSectionNav, setCollapseSidebar]);

  return (
    <>
      <aside
        className={cn(
          'sticky top-0 hidden h-screen w-72 shrink-0 overflow-x-hidden overflow-y-auto bg-neutral-100 transition-all duration-300 md:block',
          collapseSidebar && 'w-[72px]'
        )}
      >
        <div
          className={cn(
            'border-neutral-80 relative flex items-center border-b py-12',
            collapseSidebar ? 'px-2' : 'px-9'
          )}
        >
          <img
            src={collapseSidebar ? logoOnly : logo}
            alt="logo-example"
            className={cn('flex-none', collapseSidebar ? 'w-8' : 'w-28')}
          />
          <button
            className="bg-neutral-90 text-text-primary absolute right-0 cursor-pointer rounded-l-sm px-1 py-2"
            type="button"
            onClick={() => setCollapseSidebar(prev => !prev)}
          >
            <ChevronsLeft className="size-3.5" />
          </button>
        </div>

        <div className={cn('pt-6', collapseSidebar ? 'px-4' : 'px-[22px]')}>
          <CompanySelector collapsed={collapseSidebar} />
        </div>

        <nav>
          {isMenuLoading ? (
            <SidebarMenuSkeleton
              collapsed={collapseSidebar}
              rowsInSection={countPermittedMenus}
            />
          ) : (
            menuSections.map((section, sectionIndex) => {
              const visibleMenus = getVisibleMenusInSection(section.menus);

              if (visibleMenus.length === 0) return null;

              return (
                <div
                  key={section.id}
                  className="border-neutral-80 border-b last:border-b-0"
                >
                  {/* Section Label */}
                  {section.label && !collapseSidebar && (
                    <div className="text-text-tertiary px-[22px] pt-6 pb-2 text-lg font-semibold">
                      {section.label}
                    </div>
                  )}

                  <ul
                    className={cn(
                      'space-y-2 px-[22px]',
                      sectionIndex === 0 ? 'py-8' : 'pb-8',
                      section.label && !collapseSidebar ? 'pt-2' : 'pt-8'
                    )}
                  >
                    <Accordion
                      type="single"
                      collapsible
                      value={accordion ?? ''}
                      onValueChange={setAccordion}
                    >
                      {visibleMenus.map(menu => {
                        const visibleChildren = getSidebarChildren(menu);
                        const hasChildren = visibleChildren.length > 0;
                        const menuActive = isMenuActive(menu);

                        return (
                          <AccordionItem
                            key={menu.label}
                            value={menu.label}
                            className="border-0"
                          >
                            <AccordionTrigger
                              className={cn(
                                'hover:bg-primary-10 hover:text-base-orange-40 text-text-tertiary cursor-pointer border-0 p-2 hover:no-underline',
                                !hasChildren && '[&>svg]:hidden',
                                collapseSidebar && '[&>svg]:hidden',
                                menuActive &&
                                  'text-base-orange-40 bg-primary-10'
                              )}
                              onClick={() => {
                                if (collapseSidebar) {
                                  setCollapseSidebar(false);
                                }
                                if (!hasChildren) {
                                  navigate(getMenuTarget(menu));
                                } else {
                                  setAccordion(menu.label);
                                }
                              }}
                            >
                              <Tooltip>
                                <li
                                  className={cn(
                                    'flex flex-none items-center gap-3 [&>svg]:size-4',
                                    collapseSidebar && '[&>p]:hidden'
                                  )}
                                >
                                  <TooltipTrigger asChild>
                                    {menu.icon && <menu.icon />}
                                  </TooltipTrigger>
                                  <p>{menu.label}</p>
                                </li>

                                <TooltipContent side="right">
                                  {menu.label}
                                </TooltipContent>
                              </Tooltip>
                            </AccordionTrigger>

                            {hasChildren && (
                              <AccordionContent>
                                <ul>
                                  {visibleChildren.map(child => {
                                    const isActive =
                                      currentChildMenu === child.label;
                                    return (
                                      <li key={child.label}>
                                        <button
                                          type="button"
                                          onClick={() => navigate(child.route)}
                                          className={cn(
                                            'text-text-tertiary hover:text-base-orange-40 flex w-full cursor-pointer py-2 pl-9 font-medium',
                                            isActive && 'text-base-orange-40',
                                            collapseSidebar && '[&>p]:hidden'
                                          )}
                                        >
                                          <p className="flex flex-none">
                                            {child.label}
                                          </p>
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </AccordionContent>
                            )}
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </ul>
                </div>
              );
            })
          )}
        </nav>
      </aside>

      {/*
        z-40, not z-20: the open drawer is a modal layer over the page, so it has
        to outrank in-page chrome — Dashboard V2 pins its header at z-30, and at
        z-20 that header drew straight over the menu. Below the z-50 the dialogs,
        sheets and tooltips use, so a modal opened from the menu still wins.
        The `fixed` wrapper is a stacking context, so this one value places every
        layer inside the drawer, scrim and toggle included.
      */}
      <div
        className={cn(
          'relative h-0 w-0 md:hidden',
          !collapseSidebar && 'fixed z-40 block h-screen w-full bg-black/20'
        )}
      >
        <button
          className="bg-neutral-80 absolute top-[21px] -right-5 z-50 rounded-r-md px-1 py-2"
          type="button"
          onClick={() => {
            setCollapseSidebar(prev => !prev);
          }}
        >
          <ChevronsRight className="size-3" />
        </button>
        <aside
          className={cn(
            'sticky top-0 h-screen w-0 overflow-x-hidden overflow-y-auto bg-neutral-100 transition-all duration-300',
            !collapseSidebar && 'h-screen w-10/12'
          )}
        >
          <div className="border-neutral-80 relative flex min-h-32 items-center border-b px-9 py-12">
            <img
              src={collapseSidebar ? logoOnly : logo}
              alt="logo-example"
              className="w-25 flex-none"
            />
            <button
              className="bg-neutral-90 text-text-primary absolute right-0 cursor-pointer rounded-l-sm px-1 py-2"
              type="button"
              onClick={() => {
                setCollapseSidebar(prev => !prev);
              }}
            >
              <ChevronsLeft className="size-3.5" />
            </button>
          </div>

          <div className="px-[22px] pt-6">
            <CompanySelector collapsed={false} />
          </div>

          <nav>
            {isMenuLoading ? (
              <SidebarMenuSkeleton
                collapsed={false}
                rowsInSection={countPermittedMenus}
              />
            ) : (
              menuSections.map((section, sectionIndex) => {
                const visibleMenus = getVisibleMenusInSection(section.menus);

                if (visibleMenus.length === 0) return null;

                return (
                  <div
                    key={section.id}
                    className="border-neutral-80 border-b last:border-b-0"
                  >
                    {section.label && (
                      <div className="text-text-tertiary px-[22px] pt-6 pb-2 text-lg font-semibold">
                        {section.label}
                      </div>
                    )}

                    <ul
                      className={cn(
                        'space-y-2 px-[22px]',
                        sectionIndex === 0 ? 'py-8' : 'pb-8',
                        section.label ? 'pt-2' : 'pt-8'
                      )}
                    >
                      <Accordion
                        type="single"
                        collapsible
                        value={accordion ?? ''}
                        onValueChange={setAccordion}
                      >
                        {visibleMenus.map(menu => {
                          const visibleChildren = getSidebarChildren(menu);
                          const hasChildren = visibleChildren.length > 0;
                          const menuActive = isMenuActive(menu);

                          return (
                            <AccordionItem
                              key={menu.label}
                              value={menu.label}
                              className="border-0"
                            >
                              <AccordionTrigger
                                className={cn(
                                  'hover:bg-primary-10 hover:text-base-orange-40 text-text-tertiary cursor-pointer border-0 p-2 hover:no-underline',
                                  !hasChildren && '[&>svg]:hidden',
                                  menuActive &&
                                    'text-base-orange-40 bg-primary-10'
                                )}
                                onClick={() => {
                                  if (collapseSidebar) {
                                    if (hasChildren) return;
                                    setCollapseSidebar(prev => !prev);
                                  }
                                  if (!hasChildren) {
                                    setCollapseSidebar(prev => !prev);
                                    navigate(getMenuTarget(menu));
                                  } else {
                                    setAccordion(menu.label);
                                  }
                                }}
                              >
                                <li
                                  className={cn(
                                    'flex flex-none items-center gap-3 [&>svg]:size-4'
                                    // collapseSidebar && '[&>p]:hidden'
                                  )}
                                >
                                  {menu.icon && <menu.icon />}
                                  <p>{menu.label}</p>
                                </li>
                              </AccordionTrigger>

                              {hasChildren && (
                                <AccordionContent>
                                  <ul>
                                    {visibleChildren.map(child => {
                                      const isActive =
                                        currentChildMenu === child.label;
                                      return (
                                        <li key={child.label}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              navigate(child.route);
                                              setCollapseSidebar(prev => !prev);
                                            }}
                                            className={cn(
                                              'text-text-tertiary hover:text-base-orange-40 flex w-full cursor-pointer py-2 pl-9 font-medium',
                                              isActive && 'text-base-orange-40'
                                              // collapseSidebar && '[&>p]:hidden'
                                            )}
                                          >
                                            <p className="flex flex-none">
                                              {child.label}
                                            </p>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </AccordionContent>
                              )}
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </ul>
                  </div>
                );
              })
            )}
          </nav>
        </aside>
      </div>
    </>
  );
}

export { CmsSidebar };
